import { Warning } from 'phosphor-react'
import { DateTime } from 'luxon'
import React, { useCallback, useEffect, useState } from 'react'
import { Badge, Col, Form, Row } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { DeliveryFlow } from '../../../../components/Delivery/DeliveryFlow'
import DownloadPDFButton from '../../../../components/PDFReports/DownloadPDFButton'
import DeliverySummary from '../../../../components/PDFReports/DeliverySummary'
import { useLoading } from '../../../../hooks/LoadingContext'
import DashboardLayout from '../../../../layouts/Dashboard'
import api from '../../../../services/api'
import { formatDateTSX } from '../../../../utils/tsxFormat'

import styles from './styles.module.css'
import { CaretCircleDown } from 'phosphor-react'
import { isSmallDevice } from '../../../../utils/deviceScreen'
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner'

interface IVehicle {
  id: number
  company_id: number
  board: string
  model: string
  color: string
  capacity: number
  last_driver: string
  start_at: string
  note: string
  created_at: string
  updated_at: string
}

interface IRequest {
  id: number
  company_id: number
  delivery_id: number
  status: string
  invoice: string
  delivering_image_url: string
  cep: string
  city: string
  state: string
  street: string
  street_number: string
  neighborhood: string
  complement: string
  phone: string
  receiver_name: string
  weight: number
  order: number
  start_date: Date
  has_priority: boolean
  customer_rating_stars: number
  customer_rating_text: string
  request_delivery_distance_meters: number
  request_delivery_time_seconds: number
  request_delivery_driver_note: string
  delivery_had_problems: boolean
  created_at: string
  updated_at: string
  invoice_number: string
  request_number: string
}

interface IDelivery {
  id: number
  company_id: number
  vehicle_id: number
  status: string
  driver_id: number
  allocated_weight: number
  start_date: string
  note: string
  created_at: string
  updated_at: string
  requests: IRequest[]
}

function getDateDistanceFromNow(date: string) {
  const dateDistance = DateTime.fromISO(date).toRelative({ locale: 'pt-BR' })
  return dateDistance
}

const limit = 3

const Cargas: React.FC = () => {
  const [vehicles, setVehicles] = useState<IVehicle[]>([])
  const [selectedVehicle, setSelectedVehicle] = useState<IVehicle>()
  const [lastDeliveries, setLastDeliveries] = useState<IDelivery[]>([])
  const [hasmoreDeliveries, setHasMoreDeliveries] = useState<boolean>(true)

  const { isLoading, setIsLoading } = useLoading()

  useEffect(() => {
    async function loadData() {
      try {
        setIsLoading(true)
        const { data } = await api.get('/delivery-manager/vehicles?driverInfo=true')
        setVehicles(data)

        if (selectedVehicle) {
          setSelectedVehicle(data.find((v: IVehicle) => v.id === selectedVehicle.id))
        }
      } catch (err) {
        toast.error('Falha ao buscar os dados! Atualize a página.')
      }
      setIsLoading(false)
    }
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setIsLoading])

  const loadDeliveries = useCallback(async () => {
    if (!selectedVehicle) return
    if (!hasmoreDeliveries) return
    let apiUrl = `/delivery-manager/deliveries?vehicleId=${selectedVehicle.id}&creationOrder="desc"&limit=${limit}&requestsData=true`

    const offset = lastDeliveries?.length || 0

    if (offset > 0) apiUrl += `&offset=${offset}`

    try {
      setIsLoading(true)
      const { data } = await api.get(apiUrl)

      if (data.length < limit) setHasMoreDeliveries(false)

      if (!data.length && offset !== 0) {
        toast.info('Não há mais pedidos registrados')
      } else {
        setLastDeliveries((lastDeliveries) => [...(lastDeliveries || []), ...data])
      }
    } catch (err) {
      toast.error('Falha ao buscar os dados! Atualize a página.')
    }
    setIsLoading(false)
  }, [hasmoreDeliveries, selectedVehicle, setIsLoading, lastDeliveries.length])

  useEffect(() => {
    if (!lastDeliveries || lastDeliveries.length === 0) loadDeliveries()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicle])

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Cargas">
      <LoadingSpinner text="Buscando cargas. Aguarde..." />
      <Row className="d-flex align-items-center justify-content-center mt-2">
        <Col xs={8}>
          {!isLoading && vehicles.length === 0 ? (
            <Col>
              <Row
                style={{ marginInline: 30, display: 'flex' }}
                className="justify-content-center justify-content-md-start"
              >
                <Badge
                  bg=""
                  text="white"
                  style={{ textAlign: 'center', marginTop: 30, backgroundColor: '#053c5e' }}
                >
                  <Warning style={{ marginBottom: 2 }} color="#FFFFFF" size={15} weight="bold" />{' '}
                  Nenhum veículo foi encontrado!
                </Badge>
              </Row>
            </Col>
          ) : (
            <Form.Select
              className={styles.vehicleSelect}
              onChange={(e) => {
                let index: any = e.target.value
                setSelectedVehicle(vehicles[index])
                setLastDeliveries([])
                setHasMoreDeliveries(true)
              }}
            >
              <option value={-1}>Selecione o veículo</option>
              {vehicles?.map((vehicle: IVehicle, index) => (
                <option key={`selectvehicle_${index}`} value={index}>
                  {`${vehicle.model} ${
                    vehicle.color ? vehicle.color.toLowerCase() : ''
                  } | Placa ${vehicle.board}`}
                </option>
              ))}
            </Form.Select>
          )}
        </Col>
      </Row>
      {vehicles.length > 0 && (
        <Row className="d-flex align-items-center justify-content-center">
          {selectedVehicle &&
            (!isLoading && lastDeliveries.length === 0 ? (
              <Col>
                <Row
                  style={{ marginInline: 30, display: 'flex' }}
                  className="justify-content-center justify-content-md-start"
                >
                  <Badge
                    bg=""
                    text="white"
                    style={{ textAlign: 'center', marginTop: 30, backgroundColor: '#053c5e' }}
                  >
                    <Warning style={{ marginBottom: 2 }} color="#FFFFFF" size={15} weight="bold" />{' '}
                    Nenhuma carga encontrada para este veículo!
                  </Badge>
                </Row>
              </Col>
            ) : (
              <Col>
                <Row>
                  <h2 className={styles.title}>
                    {' '}
                    {isSmallDevice() ? (
                      <p style={{ marginBottom: 0, marginLeft: 15 }}>Últimas cargas do veículo</p>
                    ) : (
                      <p style={{ marginBottom: 0, marginLeft: 3 }}>Últimas cargas do veículo</p>
                    )}
                  </h2>
                </Row>
                <Row className={styles.container}>
                  {lastDeliveries.map((delivery: IDelivery, index) => (
                    <Row key={`delivery_${index}`}>
                      <Row className={styles.deliveryRow}>
                        <p className={styles.headerLabel}>
                          <strong>
                            Peso total de {delivery.allocated_weight} kg em{' '}
                            {delivery.requests.length} requisições
                          </strong>{' '}
                          -{' '}
                          <span style={{ fontSize: 13 }}>
                            Criada em: {formatDateTSX(delivery.created_at, 'dd/MM/yyyy')},{' '}
                            {getDateDistanceFromNow(delivery.created_at)}
                          </span>
                        </p>
                        {delivery.requests.length > 0 ? (
                          <>
                            <div className="mx-0 my-2 p-0">
                              <DownloadPDFButton
                                fileName={`resumo_carga_${delivery.id}.pdf`}
                                document={
                                  <DeliverySummary
                                    vehicle={selectedVehicle}
                                    delivery={delivery}
                                    requests={delivery.requests || []}
                                  />
                                }
                                text="Imprimir informações"
                              />
                            </div>

                            <DeliveryFlow
                              role="manager"
                              deliveryId={delivery.id}
                              highlightedRequestId=""
                            />
                          </>
                        ) : (
                          <div className="text-center mt-2 mb-5">
                            <span className={styles.modalInfoBadge}>
                              Não há requisições registradas para esta carga
                            </span>
                          </div>
                        )}
                      </Row>
                    </Row>
                  ))}
                </Row>
                {hasmoreDeliveries && (
                  <>
                    <p className={styles.loadMoreDeliveriesLabel}>
                      Existem mais requisições de entrega pendentes
                    </p>
                    <p className={styles.loadMoreDeliveries} onClick={() => loadDeliveries()}>
                      Carregar mais cargas <CaretCircleDown weight="fill" color="#053C5E" />
                    </p>
                  </>
                )}
              </Col>
            ))}
        </Row>
      )}
    </DashboardLayout>
  )
}

export default Cargas
